import Button from '@/shared/components/Button';
import { useEffect } from "react";
import { useSignalEffect } from "@preact/signals-react";
import { useSignals } from "@preact/signals-react/runtime";
import { useLocation } from "wouter";
import AppRouter from "@/app/router";

import { dataLoading, dataReady } from "@/app/state/app-state";
import { clearAppData, loadAppData, loadHAPOnly, loadHealth } from "@/app/api/app-api";
import { backendVersionDiffersFromFrontend } from "@/shared/utils/build-info";
import { initBedLockSync } from "@/shared/api/bed-locks";
import { initializeAppLifecycle } from "@/app/app-lifecycle";
import { initPresenceSync } from "@/shared/api/presence";
import { fetchSession, sessionReady, sessionUser } from "@/pages/authentication/state/session";
import { startSync, stopSync } from "@/shared/api/sync";
import { flushPendingUpdateReload, forceUpdateReload, hasUnsavedChanges, requestUpdateReload, updateReloadPending } from "@/shared/utils/update-reload";
import UsageAnalyticsTracker from "@/shared/components/UsageAnalyticsTracker";
import CommandPalette from "@/shared/components/CommandPalette";
import { activeShiftInstanceID, availableShiftInstances, refreshActiveShift } from "@/pages/reports/state/shift-state";

function App() {
    useSignals();
    const [location] = useLocation();
    const pathname = location.replace(/\/+$/, "") || "/";
    const authenticatedExternalHandover = pathname === "/external/handover";
    const isPublicRoute = !authenticatedExternalHandover && (pathname === "/logout" || pathname === "/hap/entry" || pathname === "/status-report" || pathname === "/telefonist" || pathname === "/bed-board" || pathname === "/external" || pathname.startsWith("/external/"));

    useEffect(() => {
		initializeAppLifecycle();
        if (!isPublicRoute) {
            void fetchSession();
        }
    }, [isPublicRoute]);

    useEffect(() => {
        let cancelled = false;
        let timer: number | null = null;

        const checkBackendVersion = async () => {
            try {
                const health = await loadHealth();
                if (!cancelled && backendVersionDiffersFromFrontend(health)) {
                    requestUpdateReload();
                    return;
                }
            } catch {
                // Ignore transient health failures and try again on the next tick.
            }

            if (!cancelled) {
                timer = window.setTimeout(() => {
                    void checkBackendVersion();
                }, 60000);
            }
        };

        void checkBackendVersion();

        return () => {
            cancelled = true;
            if (timer !== null) {
                window.clearTimeout(timer);
            }
        };
    }, []);

    useSignalEffect(() => {
        if (updateReloadPending.value && !hasUnsavedChanges.value) {
            flushPendingUpdateReload();
        }
    });

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (!hasUnsavedChanges.value) {
                return;
            }
            event.preventDefault();
            event.returnValue = true;
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, []);

    useEffect(() => {
        if (!sessionReady.value || !sessionUser.value) {
            return;
        }

        if (sessionUser.value.role === "Handover" || sessionUser.value.handover_department_id) {
            clearAppData();
            return;
        }
        if (sessionUser.value.role === "HAP") {
            clearAppData();
            void loadHAPOnly();
            return;
        }

        if (dataReady.value || dataLoading.value) {
            return;
        }
        void loadAppData();
    }, [sessionReady.value, sessionUser.value?.id, sessionUser.value?.role]);

    useEffect(() => {
        if (!sessionReady.value || !sessionUser.value || ["HAP", "Handover"].includes(sessionUser.value.role) || sessionUser.value.handover_department_id) {
            return;
        }
        initPresenceSync();
    }, [sessionReady.value, sessionUser.value]);

    useEffect(() => {
        if (!sessionReady.value || !sessionUser.value || !dataReady.value || sessionUser.value.role === "Handover" || sessionUser.value.handover_department_id) {
            stopSync();
            return;
        }
        startSync();
        return () => stopSync();
    }, [sessionReady.value, sessionUser.value, dataReady.value]);

    useEffect(() => {
        if (!sessionReady.value || !sessionUser.value || ["HAP", "Handover"].includes(sessionUser.value.role) || sessionUser.value.handover_department_id) {
            return;
        }
        initBedLockSync();
    }, [sessionReady.value, sessionUser.value]);

    useEffect(() => {
        if (!sessionReady.value || !sessionUser.value || ["HAP", "Handover"].includes(sessionUser.value.role) || sessionUser.value.handover_department_id) {
            activeShiftInstanceID.value = null;
            availableShiftInstances.value = [];
            return;
        }

        let cancelled = false;
        const refresh = () => void refreshActiveShift(() => cancelled);
        refresh();
        const timer = window.setInterval(refresh, 60_000);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
        };
    }, [sessionReady.value, sessionUser.value?.id, sessionUser.value?.role]);

    return (
        <div>
            {sessionUser.value?.role !== "Handover" && !sessionUser.value?.handover_department_id ? <UsageAnalyticsTracker /> : null}
            <CommandPalette />
            {updateReloadPending.value ? (
                <div role="status" className="fixed inset-x-0 top-0 z-[100] flex flex-wrap items-center justify-center gap-3 bg-amber-100 px-4 py-3 text-sm font-semibold text-amber-950 shadow-md">
                    <span>Update available. Save your changes before reloading.</span>
                    <Button variant="unstyled" type="button" className="rounded-md border border-amber-700 bg-white px-3 py-1.5 text-amber-900 hover:bg-amber-50" onClick={() => forceUpdateReload()}>Reload now</Button>
                </div>
            ) : null}
            <AppRouter />
        </div>
    );
}

export default App;
